import React, { useRef, useState, useEffect } from "react";
import { useUpdateEffect } from "react-use";

// Layout/UI
import { Box, Icon, IconButton, MetreaTooltip } from "@metrea-ui/v2";
// import QuantityInput from "src/components/UI/Input/QuantityInput";
import NumericInputField from "src/components/UI/Input/NumericInputField";
import styles from "./ItemOptions.module.css";
import {
	RewindRegular, // Delay: 401
	RewindFilled,
	FastForwardRegular, // Accelerate: 402
	FastForwardFilled,
	StopRegular, // Terminate: 403
	StopFilled,
	FlashPlayRegular, // New Develop: 408
	FlashPlayFilled,
} from "@fluentui/react-icons";

import { mColor } from "src/utils/themeUtils";
import { findLastNonZeroIndex } from "src/utils/dataUtils";
import { applyFormatToNumber } from "src/utils/formatUtils";
import { debugLog, showTesting } from "src/utils/debugUtils";

const ItemOptions = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const [currSelection, setCurrSelection] = useState(props.options);

	const offColor = mColor.StonyBlue;
	const disabledColor = mColor.MistyBlue;
	const onDelayColor = mColor.Electromagnetic;
	const onTerminateColor = mColor.Danger;
	const onAccelerateColor = mColor.Land;

	const [isDelay, setIsDelay] = useState(false);
	const [isTerminate, setIsTerminate] = useState(false);
	const [isAccelerate, setIsAccelerate] = useState(false);
	const [isNewDevelop, setIsNewDevelop] = useState(false);
	const [currValue, setCurrValue] = useState(0);

	const [isDelayOn, setIsDelayOn] = useState(false);
	const [isTerminateOn, setIsTerminateOn] = useState(false);
	const [isAccelerateOn, setIsAccelerateOn] = useState(false);
	const [isNewDevelopOn, setIsNewDevelopOn] = useState(false);
	const [terminatedAt, setTerminatedAt] = useState(-1);
	const [, setDelayStartAt] = useState(-1);
	const [delayEndAt, setDelayEndAt] = useState(-1);
	const [, setAccelerateStartAt] = useState(-1);
	const [accelerateEndAt, setAccelerateEndAt] = useState(-1);
	const [addCutEndAt, setAddCutEndAt] = useState(-1);

	useEffect(() => {
		debugLog(2, "[ItemOptions][FLOW] mounted", {});
	}, []);

	const getActionCursorStyle = (disabled) => ({
		cursor: disabled ? "not-allowed" : "pointer",
	});

	/**
	 * Effect: Updates "On" state and index tracking for Delay, Terminate, Accelerate, and NewDevelop icons,
	 * based on the parent-provided iconStatus prop. Ensures the correct periods are highlighted/disabled,
	 * and disables Delay/Accelerate if Terminate is selected for or before this period.
	 *
	 * @effect
	 * @dependsOn props.iconStatus, props.index
	 */
	useUpdateEffect(() => {
		// Parent provides full cross-period icon vectors; derive local period gates from them.
		props.iconStatus.forEach((x) => {
			switch (x.comp_type_id) {
				case 401:
					setIsDelayOn(x.values.reduce((a, b) => parseInt(a) + parseInt(b), 0) > 0 ? true : false);
					setDelayStartAt(x.values.indexOf(1));
					setDelayEndAt(x.values.lastIndexOf(1));
					break;
				case 403:
					setIsTerminateOn(
						x.values.reduce((a, b) => parseInt(a) + parseInt(b), 0) > 0 ? true : false,
					);
					setTerminatedAt(x.values.indexOf(1));
					if (
						x.values.reduce((a, b) => parseInt(a) + parseInt(b), 0) > 0 &&
						props.index >= x.values.indexOf(1)
					) {
						setCurrValue(0);
						// disable Terminate when there're other options selected; doens't work as expected 10/13/2023; working now?  10/30/2023
						setIsDelay(false);
						setIsAccelerate(false);
					}

					break;
				case 402:
					setIsAccelerateOn(
						x.values.reduce((a, b) => parseInt(a) + parseInt(b), 0) > 0 ? true : false,
					);
					setAccelerateStartAt(x.values.indexOf(1));
					setAccelerateEndAt(x.values.lastIndexOf(1));
					break;
				case 408:
					setIsNewDevelopOn(
						x.values.reduce((a, b) => parseInt(a) + parseInt(b), 0) > 0 ? true : false,
					);
					break;

				default:
					break;
			}
		});
	}, [props.iconStatus]);

	/**
	 * Effect: When options prop changes, updates local UI toggles (Delay, Accelerate, etc.) for this period,
	 * sets the current value for QuantityInput, and resets currSelection to reflect new options.
	 *
	 * @effect
	 * @dependsOn props.options
	 */
	useUpdateEffect(() => {
		// Rehydrate local UI state from canonical options payload whenever parent recalculates.
		props.options.forEach((x) => {
			if (x.comp_type_id === 401) setIsDelay(x.isDelay);
			else if (x.comp_type_id === 402) setIsAccelerate(x.isAccelerate);
			else if (x.comp_type_id === 403) setIsTerminate(x.isTerminate);
			else if (x.comp_type_id === 408) setIsNewDevelop(x.isNewDevelop);
			else setCurrValue(x.defaultValue * 1);
		});

		setCurrSelection([...props.options]);
		// }, [props.options, props.item]);
	}, [props.options]);

	/**
	 * Effect: If currValues is updated (and item is Procurement), finds the last non-zero Add/Cut index.
	 * Used to manage when "Terminate" is available per period.
	 *
	 * @effect
	 * @dependsOn props.currValues
	 */
	useUpdateEffect(() => {
		if (props.currValues && props.currValues.length > 0 && props.item.option_type_id === 302) {
			setAddCutEndAt(findLastNonZeroIndex(props.currValues));
		}
	}, [props.currValues]);

	/**
	 * Handler for clicking on any action icon (Delay, Terminate, Accelerate, NewDevelop).
	 * Toggles the local state for the option, then bubbles the change upward via props.onChange.
	 * Sends new flag (1 for on, 0 for off) as part of the change payload.
	 *
	 * @param {React.MouseEvent} event
	 */
	const iconClickHandler = (option) => {
		const { logicId } = option;
		debugLog(3, "[ItemOptions][ACTION] iconClickHandler", { logicId: logicId });
		let o = null;
		try {
			o = JSON.parse(logicId);
		} catch (error) {
			debugLog(2, "[ItemOptions][ERROR] iconClickHandler.invalidLogicId", { logicId, error });
			return;
		}
		debugLog(3, "[ItemOptions][ACTION] iconClickHandler.parsed", { logicId, option: o });

		let flag = false;
		// Preserve "insert vs update" semantics expected by parent reducer.
		o.update_flag = o.update_flag === 101 ? 101 : 102;
		switch (parseInt(o.comp_type_id)) {
			case 401:
				flag = !isDelay;
				setIsDelay(!isDelay);
				break;
			case 403:
				flag = !isTerminate;
				setIsTerminate(!isTerminate);
				break;
			case 402:
				flag = !isAccelerate;
				setIsAccelerate(!isAccelerate);
				break;
			case 408:
				flag = !isNewDevelop;
				setIsNewDevelop(!isNewDevelop);
				break;

			default:
				break;
		}

		props.onChange({ id: JSON.stringify(o), value: flag ? 1 : 0 });
	};

	/**
	 * Handler for QuantityInput changes.
	 * Updates local currValue, then sends the new value upward to parent via props.onChange.
	 *
	 * @param {object} option - Option object {id, value} for the changed period/option.
	 */
	const quantityInputChangeHandler = (option) => {
		const { value, logicId } = option;
		debugLog(7, "[ItemOptions][ACTION] quantityInputChangeHandler", { value: value, logicId: logicId });
		let target = null;
		try {
			target = JSON.parse(logicId);
		} catch (error) {
			debugLog(2, "[ItemOptions][ERROR] quantityInputChangeHandler.invalidLogicId", {
				logicId,
				error,
			});
			return;
		}
		debugLog(8, "[ItemOptions][ACTION] quantityInputChangeHandler.parsed", { value, target });

		// Keep update flag semantics aligned with icon updates.
		target.update_flag = target.update_flag === 101 ? 101 : 102;

		setCurrValue(value);
		props.onChange({ id: JSON.stringify(target), value: value });
	};

	const buildLogicId = (option) =>
		// Centralized payload shape used by both icon clicks and numeric commits.
		JSON.stringify({
			id: option.id,
			comp_type_id: option.comp_type_id,
			update_flag: option.update_flag,
			index: props.index,
		});

	const getIconStateConfig = (option) => {
		// Single source of truth for per-icon UI behavior.
		// Each case returns:
		// 1) whether this icon is active in the current period
		// 2) domain rule-based disabled state (mode disabled is added later in renderActionIcon)
		// 3) icon glyphs (outline vs filled)
		// 4) color resolver based on active/disabled state
		switch (option.comp_type_id) {
			case 401: {
				// Delay: blocked when terminated at/earlier period, another delay is already ON elsewhere,
				// or accelerate is ON (mutual exclusivity).
				const ruleDisabled =
					(isTerminateOn && props.index >= terminatedAt) ||
					(isDelayOn && !isDelay) ||
					isAccelerateOn;
				return {
					active: isDelay,
					ruleDisabled,
					regularIcon: RewindRegular,
					filledIcon: RewindFilled,
					getColor: ({ isDisabled }) =>
						isDelay ? onDelayColor : isDisabled ? disabledColor : offColor,
				};
			}
			case 402: {
				// Accelerate: symmetric with Delay; cannot co-exist with Delay and is blocked after Terminate.
				const ruleDisabled =
					(isTerminateOn && props.index >= terminatedAt) ||
					(isAccelerateOn && !isAccelerate) ||
					isDelayOn;
				return {
					active: isAccelerate,
					ruleDisabled,
					regularIcon: FastForwardRegular,
					filledIcon: FastForwardFilled,
					getColor: ({ isDisabled }) =>
						isAccelerate ? onAccelerateColor : isDisabled ? disabledColor : offColor,
				};
			}
			case 403: {
				// Terminate: allowed only after active delay/accelerate/add-cut windows.
				// If terminate already exists on another period, only that selected period remains actionable.
				const ruleDisabled =
					(isTerminateOn && !isTerminate) ||
					props.index <= delayEndAt ||
					props.index <= accelerateEndAt ||
					props.index <= addCutEndAt;
				return {
					active: isTerminate,
					ruleDisabled,
					regularIcon: StopRegular,
					filledIcon: StopFilled,
					getColor: ({ ruleDisabled: disabledByRule }) =>
						isTerminate ? onTerminateColor : disabledByRule ? disabledColor : offColor,
				};
			}
			case 408: {
				// New Develop: single-selection behavior; another selected period disables this one.
				const ruleDisabled = isNewDevelopOn && !isNewDevelop;
				return {
					active: isNewDevelop,
					ruleDisabled,
					regularIcon: FlashPlayRegular,
					filledIcon: FlashPlayFilled,
					getColor: ({ isDisabled }) =>
						isNewDevelop ? onAccelerateColor : isDisabled ? disabledColor : offColor,
				};
			}
			default:
				// Unknown option type -> nothing to render.
				return null;
		}
	};

	const renderActionIcon = (option) => {
		const iconConfig = getIconStateConfig(option);
		if (!iconConfig) return null;

		// Final disabled state = edit-mode gate + business-rule gate from getIconStateConfig().
		const isDisabled = props.mode !== "edit" || iconConfig.ruleDisabled;
		// Hide tooltip when action is unavailable to match legacy UX behavior.
		const tooltipTitle = iconConfig.active
			? option.comp_type_name
			: isDisabled
				? ""
				: option.comp_type_name;

		const actionTrigger = (
			<span>
				<IconButton
					// Keep the action controls visually lightweight so the icons still
					// read like inline toggle affordances.
					variant="ghost"
					padding="0"
					id={`icon_${option.id}_${option.comp_type_id}_${props.index}`}
					onClick={(evt) =>
						iconClickHandler({
							...evt,
							logicId: buildLogicId(option),
						})
					}
					{...(isDisabled ? { disabled: true } : {})}
					style={getActionCursorStyle(isDisabled)}
				>
					<Icon
						as={iconConfig.active ? iconConfig.filledIcon : iconConfig.regularIcon}
						boxSize="1.25rem"
						color={iconConfig.getColor({
							isDisabled,
							ruleDisabled: iconConfig.ruleDisabled,
						})}
					/>
				</IconButton>
			</span>
		);

		if (!tooltipTitle) return actionTrigger;

		// Keep the span trigger so disabled action buttons still have a stable hover target.
		return (
			<MetreaTooltip content={tooltipTitle}>
				{actionTrigger}
			</MetreaTooltip>
		);
	};

	// --- Logging: Render and State Snapshots ---
	if (props.item.id === 485) {
		// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
		debugLog(9, "[ItemOptions][FLOW] render", { renderCount: renderCount.current });
		debugLog(9, "[ItemOptions][FLOW] renderState", {
			props: props,
			currSelection: currSelection,
		});
		// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");
	}

	const w = props.width || (props.firstItem && props.individualLabel ? "320px" : "160px");
	return (
		<>
			<Box width={w} minWidth={w} maxWidth={w} paddingRight="1" display="flex">
				{/* First Items - Lables */}
				{props.firstItem && (
					<Box
						padding="0"
						minWidth="140px"
						flex="1 1 100%"
						display="flex"
						flexDirection="column"
						paddingBottom="15px"
					>
						<Box textAlign="center" fontWeight="600" minHeight="3rem">
							&nbsp;
						</Box>

						{currSelection &&
							currSelection.map((x, index) => {
								const isQuantityType = x.flag1 === 1;
								return (
									isQuantityType && (
										<React.Fragment key={`opt_${x.id}_${index}`}>
											{props.showPlanValue && props.individualLabel && (
												<Box textAlign="right" minWidth="140px">
													<label className={styles.label}>{x.planName}</label>
												</Box>
											)}

											{props.individualLabel && (
												<Box textAlign="right" minWidth="140px">
													<label className={styles.label}>{x.comp_type_name}</label>
												</Box>
											)}
										</React.Fragment>
									)
								);
							})}

						{/* Spacer row – must match right side flex=0 0 20px */}
						<Box flex="1" width="100%">
							&nbsp;
						</Box>

						{/* Change in Budget */}
						<Box flex="0 0 20px" width="100%" textAlign="right">
							<label className={styles.label}>{"Change in Budget"}</label>
						</Box>
						{showTesting() && (
							<Box flex="0 0 20px" width="100%" textAlign="right">
								&nbsp;
							</Box>
						)}

						{/* Inventory Rolling Total */}
						<Box flex="0 0 20px" width="100%" textAlign="right">
							<label className={styles.label}>{"Inventory Rolling Total"}</label>
						</Box>

						{/* Optional testing row */}
						{showTesting() && (
							<Box flex="0 0 20px" width="100%" textAlign="right">
								<label className={styles.label}>{"Change in Quantity"}</label>
							</Box>
						)}
					</Box>
				)}

				{/* Items */}
				<Box
					padding="0"
					width="100%"
					minWidth="100px"
					display="flex"
					flexDirection="column"
					flex="1 1 100%"
					paddingBottom="15px"
				>
					{props?.topLabel && (
						<Box textAlign="center" fontWeight="600">
							<label className={styles.topLabel}>{props.topLabel}</label>
						</Box>
					)}

					{currSelection &&
						currSelection.map((x, index) => {
							const isQuantityType = x.flag1 === 1;
							const handleNumericCommit = (value) => {
								debugLog(7, "[ItemOptions][ACTION] handleNumericCommit", {
									value: value,
									optionId: x?.id ?? null,
									index: props.index,
								});
								quantityInputChangeHandler({
									logicId: buildLogicId(x),
									value,
								});
							};

							if (isQuantityType)
								return (
									<React.Fragment key={`opt_${x.id}_${index}`}>
										{props.showPlanValue && (
											<Box key={`icon_${x.id}_${index}`} display="flex">
												{props.individualLabel && !props.firstItem && (
													<Box flex="1 1 40%" textAlign="right" minWidth="55px">
														<label className={styles.label}>{x.planName}</label>
													</Box>
												)}
												<Box
													flex="1 1 60%"
													textAlign="center"
													color={mColor.MidnightBlue}
												>
													<label className={styles.planValue}>{x.plan}</label>
												</Box>
											</Box>
										)}

										{props.individualLabel && !props.firstItem && (
											<Box flex="1 1 40%" textAlign="right" minWidth="55px">
												<label className={styles.label}>{x.comp_type_name}</label>
											</Box>
										)}

										<Box
											flex="1 1 60%"
											display="flex"
											flexDirection="column"
											justifyContent="flex-start"
											alignItems="center"
											gap="0"
											marginBottom="0"
											paddingTop="0"
										>
											<div id={`qty_${x.id}_${x.comp_type_id}_${props.index}`}>
												{/* <QuantityInput
													id={`{"id": ${x.id}, "comp_type_id": ${x.comp_type_id}, "update_flag": ${x.update_flag}}`}
													// attach JSON logic id separately to avoid parsing DOM id
													// logicId={JSON.stringify({
													// 	id: x.id,
													// 	comp_type_id: x.comp_type_id,
													// 	update_flag: x.update_flag,
													// 	index: props.index,
													// })}
													index={props.index}
													name={x.comp_type_name}
													// defaultValue={x.defaultValue || 0}
													mode={props.mode}
													// defaultValue={currValue}
													value={currValue}
													step={x.step}
													min={x.min}
													max={x.max}
													atMax={props.atMax} // pass it down
													// onChange={quantityInputChangeHandler}
													onChange={(opt) =>
														quantityInputChangeHandler({
															...opt,
															logicId: JSON.stringify({
																id: x.id,
																comp_type_id: x.comp_type_id,
																update_flag: x.update_flag,
																index: props.index,
															}),
														})
													}
													onError={props.onError}
													{...(isTerminateOn && props.index >= terminatedAt
														? { disabled: true }
														: {})}
													{...(props.mode !== "edit" ? { disabled: true } : {})}
												/> */}
												<NumericInputField
													// -----------------------------------------------------------
													// Identity / wiring
													// -----------------------------------------------------------
													id={`${x.comp_type_id}_${x.id}`} // DOM-only, logic handled below
													name="quantity"
													size="xs"
													// -----------------------------------------------------------
													// Value & constraints
													// -----------------------------------------------------------
													value={currValue}
													min={x.min}
													max={x.max}
													atMax={props.atMax} // pass it down
													step={x.step}
													// -----------------------------------------------------------
													// Behavior
													// -----------------------------------------------------------
													showControls={true}
													disabled={
														props.mode !== "edit" ||
														(isTerminateOn && props.index >= terminatedAt)
													}
													// -----------------------------------------------------------
													// Validation & commit stay inside NumericInputField so this
													// call site only handles the committed numeric result.
													// -----------------------------------------------------------
													validationMessage={`Value must be between ${x.min} and ${x.max}`}
													onError={props.onError}
													onCommit={handleNumericCommit}
													// -----------------------------------------------------------
													// Formatting (matches existing numeric behavior)
													// -----------------------------------------------------------
													inputProps={{
														formatSpec: {
															kind: "number",
															decimals: 0,
															fixed: false,
															allowNegative: Number(x.min) < 0,
														},
													}}
												/>
											</div>
										</Box>
									</React.Fragment>
								);
							else return null;
						})}

					{/* Action Icons */}
					<Box padding="0" width="100%" minWidth="100px" display="flex">
						<Box
							flex="1 1 100%"
							textAlign="right"
							display="flex"
							justifyContent="center"
							paddingTop="0.5"
							paddingBottom="0"
							margin="2"
						>
							{currSelection &&
								currSelection.map((x, index) => {
									const isQuantityType = x.flag1 === 1;
									return (
										!isQuantityType && (
											<Box key={`icon_${x.id}_${index}`} flex="0 0 auto">
												{renderActionIcon(x)}
											</Box>
										)
									);
								})}
						</Box>
					</Box>

					{props.showPlanValue && currSelection && (
						<>
							{/* Show item labels when it's not the first item */}
							{props.individualLabel && !props.firstItem && (
								<Box
									flex="1 1 40%"
									textAlign="right"
									minWidth="55px"
									paddingTop="2"
									marginBottom="3"
									display="flex"
									flexDirection="column"
								>
									<Box flex="1 1 auto">&nbsp;</Box>
									<label className={styles.label}>
										{"Change in Budget"}
										{showTesting() && (
											<>
												<br /> &nbsp;
											</>
										)}
									</label>
									<label className={styles.label}>{"Inventory Rolling Total"}</label>
									{showTesting() && (
										<>
											<label className={styles.label}>
												<br />
												{"Change in Quantity"}
											</label>
										</>
									)}
								</Box>
							)}

							{/* Display values, ex. Change in Budge, Inventory Rolling Total  */}
							<Box flex="1" width="100%">
								&nbsp;
							</Box>
							<Box flex="0 0 20px" width="100%" textAlign="center">
								<label className={styles.planValue}>
									{applyFormatToNumber(
										props.item.item_change_in_budget[props.index],
										props.item.currency_display_decimal,
										props.item.currency_symbol,
										props.item.currency_unit,
										true,
									)}
								</label>
							</Box>
							{showTesting() && (
								<Box flex="0 0 20px" width="100%" textAlign="center">
									<label className={styles.planValue} style={{ color: "lightcoral" }}>
										{props.item.item_change_in_budget[props.index]}
									</label>
								</Box>
							)}
							<Box flex="0 0 20px" width="100%" textAlign="center">
								<label className={styles.planValue}>
									{applyFormatToNumber(
										parseInt(props.item.item_rolling_total[props.index]),
										0,
										null,
										null,
										false,
									)}{" "}
								</label>
							</Box>
							{showTesting() && (
								<Box flex="0 0 20px" width="100%" textAlign="center">
									<label className={styles.planValue} style={{ color: "lightcoral" }}>
										{applyFormatToNumber(
											parseInt(props.item.item_change_in_quantity[props.index]),
											0,
											null,
											null,
											true,
										)}{" "}
									</label>
								</Box>
							)}
						</>
					)}
				</Box>
			</Box>
		</>
	);
};

export default ItemOptions;
